import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = "/api";

const BotContext = createContext(null);

export const useBot = () => {
  const ctx = useContext(BotContext);
  if (!ctx) throw new Error("useBot must be used within BotProvider");
  return ctx;
};

export const BotProvider = ({ children }) => {
  const { token } = useAuth();
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState(() => {
    try { return localStorage.getItem("edgebot_selected_bot") || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchBots = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/bots`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data.bots || [];
      setBots(data);
      // Auto-select first bot ONLY when truly nothing is selected yet, or the previously
      // selected bot no longer exists in the user's bot list. Use functional state so we
      // read the LATEST selectedBotId (not the stale closure value).
      setSelectedBotId(prev => {
        if (data.length === 0) return prev;
        if (prev && data.find(b => b.id === prev)) {
          return prev; // current selection is still valid — keep it
        }
        // Either nothing was selected, or selected bot was deleted — pick first
        const firstId = data[0].id;
        try { localStorage.setItem("edgebot_selected_bot", firstId); } catch { /* localStorage unavailable */ }
        return firstId;
      });
    } catch (err) {
      console.error("Failed to fetch bots:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBots(); }, [token]);

  const selectedBot = bots.find(b => b.id === selectedBotId) || null;
  const mode = selectedBot?.mode || "paper";

  const selectBot = (id) => {
    setSelectedBotId(id);
    try { localStorage.setItem("edgebot_selected_bot", id); } catch { /* localStorage unavailable */ }
  };

  const createBot = async (data) => {
    const res = await axios.post(`${API}/bots`, data, { headers });
    await fetchBots();
    if (res.data?.id) selectBot(res.data.id);
    return res.data;
  };

  const updateBot = async (botId, data) => {
    const res = await axios.put(`${API}/bots/${botId}`, data, { headers });
    await fetchBots();
    return res.data;
  };

  const deleteBot = async (botId) => {
    await axios.delete(`${API}/bots/${botId}`, { headers });
    await fetchBots();
  };

  return (
    <BotContext.Provider value={{
      bots, selectedBot, selectedBotId, mode, loading,
      selectBot, createBot, updateBot, deleteBot, fetchBots,
    }}>
      {children}
    </BotContext.Provider>
  );
};
